// Mui Imports
import {
  Typography,
  Grid,
  Button,
  Skeleton,
  Stack,
  Alert,
  Box,
} from "@mui/material";
import { Add } from "@mui/icons-material";

// React Imports
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// Project Imports
import "./Applications.css";
import ApplicationsGrid from "./Components/ApplicationsGrid";
import NewApplicationPanel from "./Components/NewApplicationPanel";
import { getApplications } from "src/apis/controllers/applications";
import { useLoading } from "src/hooks/useLoading";
import { logout } from "src/store/authReducer";
import { getSelectedProgramDataKey } from "util/programSelection";

const url = import.meta.env.VITE_REACT_APP_BASE_URL;

const Applications = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedProgramDataKey = getSelectedProgramDataKey();
  const [newApplication, setNewApplication] = useState(false);
  const [applications, setApplications] = useState(false);
  const [settings, setSettings] = useState({ canCreate: false, message: "" });
  const [applicationTypes, setApplicationTypes] = useState([]);
  const { loading, setLoading } = useLoading();
  const helpDocument =
    selectedProgramDataKey === "program6"
      ? {
        fileName:
          "ΛΑΕ-ΟΠΕΚΑ Επιτυχόντες-ούσες Πανελληνίων - Οδηγίες Χρήσης για την Υποβολή Αίτησης 2026.pdf",
        label:
          "Βοήθεια / Οδηγίες Χρήσης για Επιτυχόντες Πανελλαδικών",
      }
      : {
        fileName:
          "ΛΑΕ-ΟΠΕΚΑ Τρίτεκνες-Πολύτεκνες Μητέρες - Οδηγίες Χρήσης για την Υποβολή Αίτησης.pdf",
        label: "Βοήθεια / Οδηγίες Χρήσης για Δικαιούχους Γονείς",
      };

  const fetchApplications = async (dataKey) => {
    setLoading(true);
    try {
      const res = await getApplications(dataKey);
      setApplicationTypes(res?.data?.newApplicationSettings?.perType || []);
      setSettings(res?.data?.newApplicationSettings || {});
      setApplications(res?.data?.applications || []);
    } catch {
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!selectedProgramDataKey) {
      dispatch(logout());
      navigate("/", { replace: true });
      return;
    }

    setLoading(true);
    fetchApplications(selectedProgramDataKey);
  }, [dispatch, navigate, selectedProgramDataKey]);

  return (
    <div className="applications-container">
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <h2 className="bordered-heading-2">Οι Αιτήσεις μου</h2>

        <Box sx={{ display: "flex", flexDirection: "column", marginLeft: "auto", textAlign: "right" }}>
          <a
            className="ds-link small-font-size"
            href={`${url}/documents/${helpDocument.fileName}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {helpDocument.label}
          </a>
        </Box>
      </Box>
      <Box sx={{ display: "flex", mb: 2, gap: 2 }}>
        <Button
          onClick={() => setNewApplication((prev) => !prev)}
          variant="contained"
          startIcon={<Add />}
          disabled={!settings?.canCreate}
          sx={{ width: 140 }}
        >
          Νέα Αίτηση
        </Button>
        {settings?.message && !settings?.canCreate && !loading && (
          <Alert
            sx={{ padding: 0.5, flexGrow: 1 }}
            severity={settings?.cssClass || "info"}
          >
            {settings?.message ||
              "Δεν έχετε δικαίωμα δημιουργίας νέας αίτησης."}
          </Alert>
        )}
      </Box>
      {!loading ? (
        <>
          {settings?.canCreate && (
            <NewApplicationPanel
              visible={newApplication && settings?.canCreate}
              applicationTypes={applicationTypes}
              selectedProgramDataKey={selectedProgramDataKey}
            />
          )}

          <ApplicationsGrid applications={applications} />
        </>
      ) : (
        <Stack>
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </Stack>
      )}
    </div>
  );
};

export default Applications;
